import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Droplets, Box, Sun, Clock, Truck, Award, Apple } from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/k9yvnsja_IMG-20260415-WA0029.jpg";
const FIELD_WIDE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/bptukhua_FB_IMG_1776242489797.jpg";
const FACTORY_IMG = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/lwhkm2ut_FB_IMG_1776242449323.jpg";
const PRODUCT_PULP = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/9mvvrwh4_IMG-20260415-WA0050.jpg";
const PRODUCT_CHUNKS = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/9gutam0g_IMG-20260415-WA0052.jpg";
const PRODUCT_DRIED = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/4u5e08ja_IMG-20260415-WA0036.jpg";
const PRODUCT_WHOLE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/zb452am0_IMG-20260415-WA0046.jpg";

const products = [
  {
    title: "Frozen Pineapple Pulp",
    desc: "High-quality frozen pulp processed from Cayenne pineapples. Ideal for juice manufacturing, beverage blends, and food production.",
    icon: Droplets,
    image: PRODUCT_PULP,
  },
  {
    title: "Frozen Pineapple Chunks",
    desc: "Irregular-cut frozen chunks perfect for food manufacturing, ready meals, retail, and ingredient supply chains.",
    icon: Box,
    image: PRODUCT_CHUNKS,
  },
  {
    title: "Dried Pineapple",
    desc: "Dried and preserved pineapple products suitable for snack manufacturing, trail mixes, retail, and ingredient applications.",
    icon: Sun,
    image: PRODUCT_DRIED,
  },
  {
    title: "Whole Fruit Supply",
    desc: "Fresh whole Cayenne pineapples sourced directly from Mkonge Farm, available for commercial buyers, retailers, and everyday consumers.",
    icon: Apple,
    image: PRODUCT_WHOLE,
  },
];

const stats = [
  { value: "2007", label: "Established", icon: Clock },
  { value: "1964", label: "Family Farming Since", icon: Truck },
  { value: "Hluhluwe", label: "KwaZulu-Natal", icon: Award },
];

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* ===== HERO ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center animate-slow-pan"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <p
              className="font-heading text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-6 opacity-0 animate-fade-up animate-stagger-1"
            >
              Since 2007 &mdash; Hluhluwe, South Africa
            </p>
            <h1
              data-testid="hero-headline"
              className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.1] mb-6 opacity-0 animate-fade-up animate-stagger-2"
            >
              Processed Pineapple Products. Consistent Supply. Proven Quality.
            </h1>
            <p
              className="font-body text-base sm:text-lg text-white/80 leading-relaxed max-w-lg mb-10 opacity-0 animate-fade-up animate-stagger-3"
            >
              Supplying frozen pineapple pulp, chunks, dried pineapple, and
              whole fruit to manufacturers, retailers, and consumers across South Africa.
            </p>
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-up animate-stagger-4">
              <Link to="/contact">
                <Button
                  data-testid="hero-cta-primary"
                  className="btn-gold rounded-md px-8 py-6 font-heading text-sm tracking-wide font-semibold"
                >
                  Get In Touch
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/products">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="rounded-md px-8 py-6 font-heading text-sm tracking-wide border-white/30 text-white hover:bg-white/10 hover:text-white"
                >
                  View Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section data-testid="about-section" className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
                About Novapine
              </p>
              <h2
                data-testid="about-heading"
                className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6"
              >
                A Trusted Partner in Pineapple Processing
              </h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Novapine is an agro-processing company based in Hluhluwe,
                KwaZulu-Natal, South Africa. Since 2007, we have specialised in
                processing locally sourced Cayenne pineapples into high-quality
                frozen pulp, frozen chunks, and dried pineapple products.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Linked to Mkonge Farm, which cultivates all our Cayenne
                pineapples in the fertile Hluhluwe region, we maintain complete
                oversight from field to finished product. In addition to our
                processed range, whole fruit can also be supplied through
                Novapine, sourced directly from Mkonge Farm.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
                Our focus is on consistency, quality, and reliable supply for
                commercial and industrial clients, backed by a strong regional
                sourcing network.
              </p>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/a2l4zq41_IMG-20260415-WA0007.jpg"
                alt="Cayenne pineapple flowering on the plant"
                className="rounded-xl w-full h-[500px] object-contain bg-[#F9F6F0]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCTS PREVIEW ===== */}
      <section data-testid="products-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
              Our Products
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-4">
              Processed Pineapple Range
            </h2>
            <p className="font-body text-base text-novapine-brown/60 max-w-lg mx-auto">
              All products are processed from locally sourced Cayenne pineapples,
              with a focus on consistency and quality.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div
                key={product.title}
                data-testid={`product-card-${product.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="product-card bg-white border border-[#E2E0D5] rounded-xl overflow-hidden"
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-novapine-cream flex items-center justify-center">
                      <product.icon size={18} className="text-novapine-brown" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-novapine-brown">
                      {product.title}
                    </h3>
                  </div>
                  <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                    {product.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button
                data-testid="view-all-products-btn"
                className="btn-primary-novapine rounded-md px-8 py-5 font-heading text-sm tracking-wide"
              >
                View All Products
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHY CAYENNE ===== */}
      <section data-testid="cayenne-section" className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
                The Cayenne Advantage
              </p>
              <h2
                data-testid="cayenne-heading"
                className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6"
              >
                The Cayenne Pineapple
              </h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                The Cayenne is a versatile, high-quality pineapple variety — as
                exceptional for fresh eating as it is for commercial processing.
                Grown in the subtropical climate of Hluhluwe, our Cayennes
                develop an outstanding balance of sweetness, acidity, and aroma
                that makes them stand out whether eaten fresh or processed into
                pulp, chunks, and dried products.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-novapine-gold/15 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="font-heading font-bold text-sm text-novapine-brown">1</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">Exceptional Juice Content</h4>
                    <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                      Cayenne pineapples typically weigh 1.5–3 kg and contain
                      some of the highest juice-to-fibre ratios of any pineapple
                      variety. This means more flavour per fruit — whether
                      you're eating it fresh or processing it at scale.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-novapine-gold/15 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="font-heading font-bold text-sm text-novapine-brown">2</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">Versatile &amp; Delicious</h4>
                    <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                      The cylindrical shape and low-fibre flesh make the Cayenne
                      ideal for processing — but it's equally outstanding as a
                      fresh eating pineapple, with a rich, balanced flavour that
                      speaks for itself.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-novapine-gold/15 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="font-heading font-bold text-sm text-novapine-brown">3</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">A Proven Variety</h4>
                    <p className="font-body text-sm text-novapine-brown/60 leading-relaxed">
                      The Cayenne is one of the most widely grown pineapple
                      varieties in the world, trusted by producers, retailers,
                      and consumers alike for its consistent quality and
                      flavour — and it's what we grow right here on Mkonge Farm.
                    </p>
                  </div>
                </div>
              </div>
              <p className="font-body text-sm text-novapine-brown/50 italic">
                Whether you're a manufacturer or a pineapple lover — Cayenne
                delivers.
              </p>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/4nrt6yn5_IMG-20260415-WA0009.jpg"
                alt="Cayenne pineapple cross-section"
                className="rounded-xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== CREDIBILITY / STATS ===== */}
      <section data-testid="credibility-section" className="py-24 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
              Why Novapine
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
              Built on Experience &amp; Reliability
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat) => (
              <div
                key={stat.label}
                data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="stat-card bg-white/5 border border-white/10 rounded-xl p-8 text-center"
              >
                <stat.icon size={28} className="text-novapine-gold mx-auto mb-4" />
                <p className="font-heading text-3xl font-bold text-white mb-2">
                  {stat.value}
                </p>
                <p className="font-body text-sm text-white/60">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CURRENTLY SUPPLYING ===== */}
      <section data-testid="suppliers-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
              Trusted By
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown">
              Currently Supplying
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {["Sir Fruit", "NuLeaf", "Mr Juice", "Euroberry"].map((name) => (
              <div
                key={name}
                className="bg-white border border-[#E2E0D5] rounded-xl px-8 py-4 transition-all duration-300 hover:border-novapine-gold/40 hover:shadow-md"
              >
                <span className="font-heading font-semibold text-lg text-novapine-brown">
                  {name}
                </span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="font-body text-base text-novapine-brown/60">
              Also supplying all major fresh produce markets in South Africa,
              including Johannesburg, Durban, Pretoria, and Springs.
            </p>
          </div>
        </div>
      </section>

      {/* ===== TARGET MARKET ===== */}
      <section data-testid="market-section" className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
              Who We Serve
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown">
              Industries We Supply
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              "Juice Manufacturers",
              "Food & Beverage Manufacturers",
              "Ingredient Buyers & Distributors",
              "Snack & Health Food Producers",
              "Retailers & Everyday Consumers",
            ].map((market) => (
              <div
                key={market}
                data-testid={`market-card-${market.toLowerCase().replace(/[&\s]+/g, '-')}`}
                className="bg-white border border-[#E2E0D5] rounded-xl p-6 text-center transition-all duration-300 hover:border-novapine-gold/40 hover:shadow-md"
              >
                <p className="font-heading font-semibold text-base text-novapine-brown">
                  {market}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FARM BANNER ===== */}
      <section className="relative h-[300px] md:h-[400px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center animate-slow-pan"
          style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/0yt59mna_IMG-20260415-WA0021.jpg)` }}
        />
        <div className="absolute inset-0 bg-novapine-brown/50" />
        <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
          <div>
            <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-3">
              Mkonge Farm &mdash; Hluhluwe
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
              From Our Fields to Your Table
            </h2>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section data-testid="cta-section" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-novapine-brown mb-6">
            Ready to Discuss Supply?
          </h2>
          <p className="font-body text-base text-novapine-brown/60 leading-relaxed mb-10 max-w-lg mx-auto">
            Get in touch with our team to request product specifications,
            discuss volume requirements, or enquire about becoming a supply partner.
          </p>
          <Link to="/contact">
            <Button
              data-testid="final-cta-button"
              className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold"
            >
              Enquire About Supply
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
